import { getWebViewerLoadDocumentOptions, getWebViewerPreviewKind, isSpreadsheetPreviewExtension } from './filePreviewUtils';

const EDITOR_MODE_PICKER_ELEMENTS = [
    'editorDropdown',
    'officeEditorModeDropdown',
    'editModeDropdown',
];

const DOCX_EDITING_UI_ELEMENTS = [
    'tools-header',
    'officeEditorHomeGroupedItems',
    'officeEditorLayoutGroupedItems',
    'officeEditorInsertGroupedItems',
    'office-editor-default-ribbon-group',
    'saveButton',
    'filePickerButton',
];

function hideEditorModePicker(instance) {
    instance.UI.disableElements(EDITOR_MODE_PICKER_ELEMENTS);
}

function scheduleHideEditorModePicker(instance) {
    hideEditorModePicker(instance);
    window.requestAnimationFrame(() => hideEditorModePicker(instance));
    window.setTimeout(() => hideEditorModePicker(instance), 250);
}

function lockDocxViewOnly(instance) {
    const { Core } = instance;
    const officeEditor = Core.documentViewer.getDocument()?.getOfficeEditor?.();
    const viewOnly = Core.Document?.OfficeEditor?.EditMode?.VIEW_ONLY;
    if (officeEditor && viewOnly !== undefined) {
        officeEditor.setEditMode(viewOnly);
        return true;
    }
    return false;
}

function hideDocxEditingChrome(instance) {
    scheduleHideEditorModePicker(instance);

    if (typeof instance.UI.enableViewOnlyMode === 'function') {
        instance.UI.enableViewOnlyMode();
    }

    instance.UI.disableElements(DOCX_EDITING_UI_ELEMENTS);

    if (!lockDocxViewOnly(instance)) {
        window.setTimeout(() => lockDocxViewOnly(instance), 100);
        window.setTimeout(() => lockDocxViewOnly(instance), 500);
    }

    window.setTimeout(() => {
        scheduleHideEditorModePicker(instance);
        instance.UI.disableElements(DOCX_EDITING_UI_ELEMENTS);
    }, 300);
}

function lockNativeEditorViewOnly(instance, kind) {
    if (kind === 'docx') {
        hideDocxEditingChrome(instance);
    }
}

/**
 * Make WebViewer default to fit-page-width (100% of viewer width).
 * Call once after the instance is created.
 */
export function configureWebViewerFitWidth(instance) {
    const { Core, UI } = instance;
    const { documentViewer } = Core;

    documentViewer.defaults.FitMode = documentViewer.FitMode.FitWidth;

    const fitToWidth = () => {
        UI.setFitMode(UI.FitMode.FitWidth);
    };

    documentViewer.addEventListener('documentLoaded', () => {
        fitToWidth();
        // Some PDFs include Open Actions that reset fit after load.
        requestAnimationFrame(fitToWidth);
    });
}

/**
 * Shared WebViewer setup for dataroom / attachment / AI Warden previews.
 */
export function setupWebViewerPreviewInstance(instance, { extension, fileUrl, filename, onReady, onDocumentLoaded, onDocumentLoadError, isDisposed }) {
    const kind = getWebViewerPreviewKind(extension);
    const isNativeEditor = kind === 'docx';
    const { Core } = instance;
    let readyCalled = false;
    const markReady = () => {
        if (readyCalled || isDisposed?.()) return;
        readyCalled = true;
        onReady?.();
    };

    if (isNativeEditor) {
        hideDocxEditingChrome(instance);
        markReady();
    } else {
        markReady();
        configureWebViewerFitWidth(instance);
        instance.UI.disableElements(['toolsHeader', 'ribbons']);
    }

    instance.UI.disableElements(['toggleNotesButton', 'menuButton']);

    Core.documentViewer.addEventListener('documentLoadError', error => {
        if (!isDisposed?.()) {
            onDocumentLoadError?.(error);
            markReady();
        }
    });

    Core.documentViewer.addEventListener('documentLoaded', () => {
        if (isDisposed?.()) return;
        lockNativeEditorViewOnly(instance, kind);
        markReady();
        onDocumentLoaded?.(instance, kind);
    });

    if (fileUrl && isSpreadsheetPreviewExtension(extension)) {
        instance.UI.loadDocument(fileUrl, getWebViewerLoadDocumentOptions({ extension, filename }));
    }
}
